/** Trip-agnostic data model. The Croatia trip is just one TripDefinition. */

export interface Act {
  name: string;
  cat?: string;
  /** duration options in hours; durs[min(1, len-1)] is the default */
  durs: number[];
  price?: string;
  hours?: string;
  addr?: string;
  web?: string;
  tel?: string;
  badge?: "book" | "free";
  /** booking instructions */
  book?: string;
  notes?: string;
  /** "Getting there" HTML with operator links */
  transport?: string;
  slotnote?: string;
  /** Wikipedia title for the details-card photo */
  wiki?: string;
  /** fixed start "HH:MM" — tour departures can't start earlier */
  fs?: string;
  src?: string[];
  /** sentinel entries (skip/freetime) with no details card */
  nomodal?: boolean;
}

export interface FixedItem {
  /** stable identity across structural edits — assigned on first edit, so
   * state remapping is a lookup rather than positional guesswork */
  uid?: string;
  f: 1;
  t: string;
  e?: string;
  ic: string;
  l: string;
  badge?: string;
  n?: string;
  /** arriving at a lodging: resets the travel origin */
  at?: string;
  /** details card for a fixed item */
  det?: string;
  /** hard deadline: warn when the plan overruns it */
  dl?: 1;
  dlmsg?: string;
  /** route-map waypoint (ferry pier, airport, station) — curated in extract.ts */
  g?: [number, number];
  /** this step arrives by sea/air — route maps draw a dashed crossing, not a street route */
  sea?: 1;
  /** booking-checklist key — the row shows a live booked/needs-booking flag */
  blk?: string;
}

export interface SlotItem {
  /** stable identity across structural edits (see FixedItem.uid) */
  uid?: string;
  s: string;
  opts: string[];
  def?: string;
  /** default duration override (hours) */
  dd?: number;
  /** earliest start "HH:MM" (meals anchor: lunch ≥12:15, dinner ≥20:00) */
  min?: string;
  note?: string;
}

export type DayItem = FixedItem | SlotItem;
export const isFixed = (it: DayItem): it is FixedItem => (it as FixedItem).f === 1;

export interface DayDef {
  id: string;
  dow: string;
  /** display date without a year, e.g. "Sep 12" — `dow`/`date` are what render */
  date: string;
  /** YYYY-MM-DD. Optional so the extracted Croatia definition stays valid;
   * set on every day created or edited in-app, and the unambiguous source for
   * date maths (`date` alone can't tell two Septembers apart). */
  iso?: string;
  loc: string;
  trav?: 1;
  title: string;
  start: string;
  blurb: string;
  items: DayItem[];
}

export interface BLDef {
  k: string;
  req?: string[];
  always?: 1;
  when: string;
  cls: string;
  what: string;
  why: string;
  url: string;
  link: string;
}

export interface TravelInfo {
  min: number;
  icon: string;
}

/** One segment of the hero route line: a home endpoint, a transport icon, or an overnight stop. */
export interface HeroRouteSeg {
  /** non-overnight endpoint, e.g. "Nashville", "home" */
  place?: string;
  /** transport icon between stops: ✈ ⛴ → 🚆 … */
  via?: string;
  /** overnight stop, rendered bold */
  stop?: string;
  nights?: number;
}

/** Per-trip hero banner content — title, travelers/dates line, route summary. */
export interface HeroDef {
  title: string;
  /** e.g. "Matt & Katerina · September 12 – 23, 2026" */
  sub: string;
  route: HeroRouteSeg[];
}

export interface TripDefinition {
  name: string;
  hero?: HeroDef;
  /** default back-at-base target (minutes since midnight); per-day overrides live in state */
  dayEnd: number;
  /** minutes past target before the day is flagged late (reference used 20; now 45) */
  lateGrace?: number;
  /** act ids exempt from duplicate detection */
  dupExempt: string[];
  act: Record<string, Act>;
  days: DayDef[];
  bl: BLDef[];
  /** actId → [lat, lng, regionCode] */
  geo: Record<string, [number, number, string]>;
  /** [pinNumber, label, lat, lng] */
  stays: [number, string, number, number][];
  /**
   * Ids of app-managed stays, aligned with the TAIL of stays[] (curated rows
   * from extraction come first and have no id). The id is the hotelPts key —
   * what dayHome and FixedItem.at reference. Optional so extracted
   * definitions stay valid; validation requires the alignment when present.
   */
  stayIds?: string[];
  route: [number, number][];
  hotelPts: Record<string, [number, number]>;
  /** dayId → [startStayId, endStayId] */
  dayHome: Record<string, [string, string]>;
  noEnd: Record<string, number>;
  /** fixed travel-time overrides for boat/train legs */
  boat: Record<string, TravelInfo>;
  /** region colors + labels for the map filters */
  rc: Record<string, string>;
  rnames: Record<string, string>;
  /**
   * Provenance per activity id. Absent or "extracted" means the extraction
   * pipeline owns it — the app refuses to edit those, because the next
   * `npm run extract` would silently undo the change. Entries added in-app
   * ("app"/"ai") are the ones a definition-only import must preserve.
   */
  actOrigin?: Record<string, "extracted" | "app" | "ai">;
  /**
   * IANA timezone per region code (e.g. { ams: "Europe/Amsterdam" }).
   * Optional — the scheduler only warns about a timezone change when both
   * sides of a transition have one AND their UTC offsets actually differ, so
   * a missing map simply produces no warnings.
   */
  tz?: Record<string, string>;
  /**
   * Informational cards below the itinerary — tips, culture, look-outs.
   * Structured text on purpose: items render ESCAPED, so a definition that
   * came through the AI can never carry markup.
   */
  sections?: { title: string; items: string[] }[];
  /**
   * Curated prose cards, rendered as RAW HTML — the hand-written guides that
   * carry links, <details> and formatting no structured-text field can express
   * (Croatia's "Getting around", "Tips & gotchas", "Culture & etiquette").
   *
   * This is trusted content and the trust is enforced by there being no way
   * to write it except the two trusted paths: `npm run extract` (which builds
   * it from the reference document) and the admin import that ships that
   * output. **No def-op writes `prose`, so neither the AI surfaces nor a
   * co-planner's editor can ever put markup here** — that fence is the reason
   * it can be rendered unescaped, and `sections` above stays the only thing
   * AI-authored content may fill. A test asserts the fence holds.
   */
  prose?: { title: string; html: string }[];
}

/** 0 Deciding · 1 Confirmed · 2 Booked */
export type Status = 0 | 1 | 2;

export interface SlotValue {
  act: string;
  dur: number;
  st: Status;
}

/** keyed `${dayId}_${itemIndex}` */
export type Slots = Record<string, SlotValue>;
/** keyed by checklist k (or `auto_${actId}`) */
export type Checks = Record<string, boolean>;
/** per-day "back at base by" target, minutes since midnight; keyed by dayId */
export type DayEnds = Record<string, number>;
/**
 * User-set duration (minutes) for an open-ended fixed item — e.g. "passport
 * control, budget 2h". Keyed `${dayId}_${itemIndex}`, same scheme as Slots but
 * a separate namespace (an index is never both a fixed item and a flex slot).
 * A value of 0 or absent means "no override" — falls back to the item's own
 * `t`/`e`, matching the DayEnds precedent of sparse, optional, not-seeded state.
 */
export type FixDurs = Record<string, number>;

export interface TripState {
  slots: Slots;
  checks: Checks;
  dayEnds: DayEnds;
  fixDurs: FixDurs;
}
